import React from 'react';
import {
  MagnifyingGlassIcon,
  ChartPieIcon,
  PlusIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

/**
 * Step2ReferenceDecision
 *
 * Calls onSelectReferenceDecision with:
 *   true       → Path A: reference a past Won engagement (Step3SelectTemplate)
 *   false      → Path B: use a baseline template (Step3BaselineSelection)
 *   'scratch'  → Path C: skip Step 3, go straight to Step4BuildServices blank
 *
 * All three options call onNextStep() so the parent wizard advances.
 * The parent uses the referenceDecision value to decide WHICH step 3 to render.
 */
const Step2ReferenceDecision = ({
  pricingContext,
  wizardData,
  isLoadingEngagements,
  realEngagementsCount,
  onSelectReferenceDecision,
  onNextStep,
}) => {
  const handleSelect = (decision) => {
    onSelectReferenceDecision(decision);
    onNextStep();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Do you want to reference a past engagement?
        </h2>
        <p className="text-sm sm:text-base text-gray-600">
          Using historical data can improve accuracy and save time
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">

        {/* ── Path A: Reference past work ── */}
        <button
          data-tour="step2-reference"
          onClick={() => handleSelect(true)}
          className="p-6 sm:p-8 border-2 border-gray-200 rounded-lg hover:border-violet-500 hover:shadow-lg transition-all text-center group"
        >
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-violet-200 transition-colors">
            <MagnifyingGlassIcon className="w-7 h-7 sm:w-8 sm:h-8 text-violet-600" />
          </div>
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">
            Yes, Reference Past Work
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 mb-4">
            Search and filter Won engagements to find similar projects
          </p>
          {isLoadingEngagements ? (
            <div className="text-xs text-gray-500">Loading engagements...</div>
          ) : (
            <div className="text-xs font-medium text-darkblue-600">
              {realEngagementsCount} Won engagements available
            </div>
          )}
        </button>

        {/* ── Path B: Use baseline ── */}
        <button
          onClick={() => handleSelect(false)}
          className="p-6 sm:p-8 border-2 border-gray-200 rounded-lg hover:border-violet-500 hover:shadow-lg transition-all text-center group"
        >
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-violet-200 transition-colors">
            <ChartPieIcon className="w-7 h-7 sm:w-8 sm:h-8 text-violet-600" />
          </div>
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">
            No, Use Baseline
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 mb-4">
            Choose from standard service templates based on historical client profiles
          </p>
          <div className="text-xs font-medium text-darkblue-600">
            Model · High · Medium · Low · Custom
          </div>
        </button>

        {/* ── Path C: Start from scratch ── */}
        <button
          onClick={() => handleSelect('scratch')}
          className="p-6 sm:p-8 border-2 border-gray-200 rounded-lg hover:border-violet-500 hover:shadow-lg transition-all text-center group"
        >
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-violet-200 transition-colors">
            <PlusIcon className="w-7 h-7 sm:w-8 sm:h-8 text-violet-600" />
          </div>
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">
            Start from Scratch
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 mb-4">
            Build a completely custom bid with no pre-populated data
          </p>
          <div className="text-xs font-medium text-darkblue-600">
            Maximum flexibility
          </div>
        </button>
      </div>

      {/* Market Context Preview */}
      {pricingContext && (
        <div className="max-w-3xl mx-auto mt-6 sm:mt-8 p-4 sm:p-6 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm sm:text-base font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <InformationCircleIcon className="w-5 h-5" />
            Market Context Preview
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-center">
            {[
              { label: 'Minimum',       value: pricingContext.min    },
              { label: 'Median',        value: pricingContext.median },
              { label: 'Upper Quartile',value: pricingContext.upper  },
              { label: 'Maximum',       value: pricingContext.max    },
            ].map(({ label, value }) => (
              <div key={label}>
                <div className="text-xs text-blue-700 mb-1">{label}</div>
                <div className="text-base sm:text-lg font-bold text-blue-900">
                  ${value.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-xs text-blue-700 text-center">
            Based on {pricingContext.sampleSize} past {wizardData.engagementType}s
          </div>
        </div>
      )}
    </div>
  );
};

export default Step2ReferenceDecision;